// these sizes are arbitrary and you can set them to whatever you wish
import { css } from 'styled-components'

export const breakpoints = {
  giant: 1250,
  desktop: 1080,
  tablet: 768,
  phone: 450,
  phoneMd: 375,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  xxxl: 1600,
  xwide: 1920,
}

export const pixelsToEms = (size: number) => `${size / 16}em`

type BreakpointName = keyof typeof breakpoints

type CSSMixinArgs = Parameters<typeof css>

type MediaMixinMap = {
  [name in BreakpointName]: typeof css
}

export const media = Object.keys(breakpoints).reduce(
  (acc: MediaMixinMap, label: BreakpointName): MediaMixinMap => {
    const mixin = (...args: CSSMixinArgs) => css`
      @media (min-width: ${pixelsToEms(breakpoints[label])}) {
        ${css(...args)}
      }
    `
    /*
      Note: Casting to type to preserve generics, so that component props can still be passed
      to our mixin function. Otherwise nested calls to the css function cannot receive component
      prop types -- it's tucked away inside styled-components typings under the callable interface
      `BaseThemedCssFunction`.

      e.g. this is valid syntax:
        css<{ customColor: string }>`
          color: ${({ customColor }) => customColor || 'black' };
        `
      and we want to be able to do the same for
        media.md<{ customColor: string }>`
          color: ${({ customColor }) => customColor || 'black' };
        `
    */
    acc[label] = mixin as typeof css

    return acc
  },
  {} as MediaMixinMap
)

export const mediaMax = Object.keys(breakpoints).reduce(
  (acc: MediaMixinMap, label: BreakpointName): MediaMixinMap => {
    const mixin = (...args: CSSMixinArgs) => css`
      @media (max-width: ${pixelsToEms(breakpoints[label] - 1)}) {
        ${css(...args)}
      }
    `

    acc[label] = mixin as typeof css

    return acc
  },
  {} as MediaMixinMap
)

type MediaQueryMap = {
  [name in keyof typeof breakpoints]: () => string
}

export const mediaQuery = Object.keys(breakpoints).reduce(
  (acc: MediaQueryMap, label: BreakpointName): MediaQueryMap => {
    acc[label] = () => `(min-width: ${pixelsToEms(breakpoints[label])})`

    return acc
  },
  {} as MediaQueryMap
)

export const mediaMaxQuery = Object.keys(breakpoints).reduce(
  (acc: MediaQueryMap, label: BreakpointName): MediaQueryMap => {
    acc[label] = () => `(max-width: ${pixelsToEms(breakpoints[label] - 1)})`

    return acc
  },
  {} as MediaQueryMap
)

export default media
